import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";
import defaultImage from "../../../assets/images/default.jpg";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";

// Creator boosts (2026-08-15): reels boosted into the feed / livestreams
// pinned to the top of the live list. Coin-escrowed, instant-on (no review
// step — reels are already approval-gated, lives can't wait). 2 coins per
// delivered impression. Admin can force-cancel (refunds the unspent budget).
const STATUSES = ["active", "paused", "completed", "cancelled"];
const TYPES = ["all", "reel", "live"];
const statusLabel = (s) =>
  ({ active: "Active", paused: "Paused", completed: "Completed", cancelled: "Cancelled" }[s] || s);

const BoostsAdmin = () => {
  const [status, setStatus] = useState("active");
  const [type, setType] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ spentCoins: 0, budgetCoins: 0, deliveries: 0 });
  const [busy, setBusy] = useState("");

  const load = () =>
    apiInstanceFetch
      .get(
        `boost/admin/list?status=${status}${type === "all" ? "" : `&targetType=${type}`}&page=1&limit=200`
      )
      .then((r) => {
        if (r?.status) {
          setRows(r.data || []);
          setTotal(r.total || 0);
          setTotals(r.totals || { spentCoins: 0, budgetCoins: 0, deliveries: 0 });
        }
      })
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, type]);

  const forceCancel = (b) => {
    const reason = window.prompt(
      "Force-cancel this boost? Unspent coins are refunded to the owner.\nOptional reason:"
    );
    if (reason === null) return; // dismissed
    setBusy(b._id);
    apiInstanceFetch
      .post(`boost/admin/cancel`, { boostId: b._id, reason })
      .then((r) => {
        if (r?.status) load();
        else alert(r?.message || "Action failed");
      })
      .catch(() => alert("Action failed"))
      .finally(() => setBusy(""));
  };

  const owner = (r) => {
    const u = r.ownerUserId || {};
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown";
  };

  const chip = (active) => ({
    border: `1px solid ${active ? INK : HAIRLINE}`,
    background: active ? INK : "#FFF",
    color: active ? "#FFF" : INK,
    fontWeight: 600,
  });

  return (
    <div className="mainCategory">
      <Title name="Ads — Boosts" />

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, margin: "16px 0" }}>
        <i className="fa-solid fa-rocket" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>
          {statusLabel(status)} ({total})
        </span>
        <span style={{ fontSize: 12.5, color: MUTED, marginLeft: 10 }}>
          {totals.deliveries.toLocaleString()} deliveries · {totals.spentCoins.toLocaleString()} /{" "}
          {totals.budgetCoins.toLocaleString()} coins spent
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)} className="btn btn-sm" style={chip(type === t)}>
              {t === "all" ? "All types" : t === "reel" ? "Videos" : "Lives"}
            </button>
          ))}
          <span style={{ width: 10 }} />
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} className="btn btn-sm" style={chip(status === s)}>
              {statusLabel(s)}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 14,
            padding: 48,
            textAlign: "center",
            color: MUTED,
          }}
        >
          <i className="fa-solid fa-rocket" style={{ fontSize: 34, color: HAIRLINE }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>
            No {statusLabel(status).toLowerCase()} boosts
          </div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
          <table className="table align-middle" style={{ margin: 0, minWidth: 860 }}>
            <thead>
              <tr style={{ background: CREAM }}>
                {["Type", "Content", "Owner", "Budget", "Spent", "Reached", "Status", "Created", "Action"].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      color: MUTED,
                      padding: "10px 12px",
                      borderBottom: `2px solid ${HAIRLINE}`,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={{ padding: "8px 12px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        background: r.targetType === "reel" ? "#FFF6DA" : "#E3EDFB",
                        color: r.targetType === "reel" ? GOLD : "#1D4F91",
                        borderRadius: 20,
                        padding: "3px 10px",
                        fontSize: 11.5,
                        fontWeight: 700,
                      }}
                    >
                      {r.targetType === "reel" ? "Video" : "Live"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 12px", maxWidth: 220 }}>
                    {r.targetType === "reel" && r.reelId ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img
                          src={r.reelId.thumbnail || defaultImage}
                          alt=""
                          onError={(e) => (e.currentTarget.src = defaultImage)}
                          style={{ width: 34, height: 48, objectFit: "cover", borderRadius: 6 }}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 12,
                              color: INK,
                              fontWeight: 600,
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.reelId.description || "Untitled reel"}
                          </div>
                          <div style={{ fontSize: 11, color: MUTED }}>
                            {(r.reelId.viewCount || 0).toLocaleString()} views
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: MUTED }}>
                        {r.targetType === "live" ? "All their livestreams" : "Deleted reel"}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 13 }}>{owner(r)}</td>
                  <td style={{ padding: "8px 12px" }}>{(r.budgetCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px" }}>{(r.spentCoins || 0).toLocaleString()} 🪙</td>
                  <td style={{ padding: "8px 12px" }}>{(r.deliveriesCount || 0).toLocaleString()}</td>
                  <td style={{ padding: "8px 12px", fontSize: 12, textTransform: "capitalize" }}>
                    {statusLabel(r.status)}
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {dayjs(r.createdAt).format("DD MMM YYYY")}
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {r.status === "active" || r.status === "paused" ? (
                      <button
                        disabled={busy === r._id}
                        onClick={() => forceCancel(r)}
                        className="btn btn-sm"
                        style={{ background: "#FDECEC", color: RED, fontWeight: 700, border: "none" }}
                      >
                        Cancel & refund
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: MUTED }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BoostsAdmin;
